import { Batch } from '../types/types';
import { getD2CBaseURL } from '../utils/env-utils';
import { log, logErr } from '../utils/logger';
import { DEFAULT_TIMEOUT_MILLIS, INTERNAL_SERVER_ERROR } from '../constants/clients';

interface SendBatchProps {
  batch: Batch;
  retries?: number;
  keepalive: boolean;
  signal?: AbortSignal;
}

export class D2CClientRequestHandler {
  private readonly baseUrl: string;
  private readonly defaultTimeoutMillis: number;

  constructor() {
    this.baseUrl = getD2CBaseURL();
    this.defaultTimeoutMillis = DEFAULT_TIMEOUT_MILLIS; // Adjust after reviewing with team
  }

  /**
   * A generic wrapper for making POST calls
   * @param endpoint - The path to which to make the request (we append this to base url)
   * @param options - The options for the POST call, fetch style
   * @return a Promise for the response
   */
  post(endpoint: string, options: Record<string, unknown>): Promise<Response> {
    return fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...options,
    });
  }

  /**
   * Sends a batch of events, including common data, to the API.
   * @param endpoint - The path to which to send the batch (we append this to base url)
   * @param options - The options for the POST call, fetch style
   * @param maxRetries - The maximum number of retries remaining, for recoverable errors
   * @param timeoutMillis - how many milliseconds to wait before determining that a timeout has occurred
   * @return a Promise for the response
   */
  async sendBatch(endpoint: string, options: SendBatchProps, maxRetries: number, timeoutMillis?: number): Promise<void | Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
    }, timeoutMillis || this.defaultTimeoutMillis);

    const body = JSON.stringify(options.batch);
    log(`Batch POST request body: ${body}`);
    try {
      const response = await this.post(endpoint, { ...options, body: body, signal: controller.signal });
      if (isRecoverableError(response) && maxRetries > 0 && navigator.onLine && !options.keepalive) {
        return this.sendBatch(endpoint, options, maxRetries - 1, timeoutMillis);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError' && maxRetries > 0) {
        logErr(error);

        return this.sendBatch(endpoint, options, maxRetries - 1);
      }

      logErr(error);
    } finally {
      clearTimeout(timeout);
    }
  }
}

/**
 * Checks whether an error is recoverable, i.e., whether retrying it is meaningful.
 * @param response - The response returned from fetch
 * @return whether the response indicates a recoverable error
 */
const isRecoverableError = function (response: Response): boolean {
  return response && !response.ok && response.status >= INTERNAL_SERVER_ERROR;
};
